import { useMemo } from "react";
import { motion } from "framer-motion";
import { User, Users, BookOpen, Languages, Sparkles, Building2, ArrowRight, Video } from "lucide-react";

const services = [
  // Première ligne - 4 services
  {
    icon: User,
    title: "Cours Particuliers",
    description: "Accompagnement personnalisé adapté au rythme de chaque élève.",
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    borderColor: "hover:border-blue-300",
    row: 1
  },
  {
    icon: Users,
    title: "Cours Collectifs",
    description: "Petits groupes dynamiques pour apprendre ensemble.",
    color: "bg-orange-50",
    iconColor: "text-orange-600",
    borderColor: "hover:border-orange-300",
    row: 1
  },
  {
    icon: Video,
    title: "Cours en Visio",
    description: "Cours à distance flexibles depuis votre domicile.",
    color: "bg-indigo-50",
    iconColor: "text-indigo-600",
    borderColor: "hover:border-indigo-300",
    row: 1
  },
  {
    icon: BookOpen,
    title: "Stages de Révisions",
    description: "Stages intensifs pendant les vacances scolaires.",
    color: "bg-green-50",
    iconColor: "text-green-600",
    borderColor: "hover:border-green-300",
    row: 1,
    tallyFormId: "81rrAr"
  },
  // Deuxième ligne - 3 services
  {
    icon: Languages,
    title: "English Class",
    description: "Cours d'anglais ludiques pour enfants dès 3 ans jusqu'au CM2.",
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    borderColor: "hover:border-purple-300",
    badge: "Dès 3 ans",
    row: 2,
    link: "/english-class"
  },
  {
    icon: Sparkles,
    title: "Scolarité Atypique",
    description: "Accompagnement spécialisé pour élèves DYS, HPI, etc.",
    color: "bg-pink-50",
    iconColor: "text-pink-600",
    borderColor: "hover:border-pink-300",
    row: 2
  },
  {
    icon: Building2,
    title: "Partenariat Entreprise",
    description: "Solutions sur-mesure pour les entreprises.",
    color: "bg-cyan-50",
    iconColor: "text-cyan-600",
    borderColor: "hover:border-cyan-300",
    row: 2
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export const Services = () => {
  // Memoize filtered services to avoid recalculation on every render
  const row1Services = useMemo(() => services.filter(s => s.row === 1), []);
  const row2Services = useMemo(() => services.filter(s => s.row === 2), []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-14 md:py-28 relative bg-white"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        {/* Bannière Avance Immédiate - Tape à l'oeil */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 md:mb-16 relative overflow-hidden"
        >
          {/* Background avec animation */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-3xl" />
          <div className="absolute inset-0 opacity-20 rounded-3xl" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
          
          <div className="relative p-5 sm:p-8 md:p-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">
              {/* Left side - Big 50% */}
              <div className="flex items-center gap-4 sm:gap-6">
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="relative flex-shrink-0"
                >
                  <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 bg-white rounded-2xl flex items-center justify-center shadow-2xl">
                    <div className="text-center">
                      <span className="text-3xl sm:text-5xl md:text-6xl font-black bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">50%</span>
                      <div className="text-[9px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Économisé</div>
                    </div>
                  </div>
                  {/* Sparkle effects */}
                  <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-yellow-300 rounded-full animate-ping" />
                  <div className="absolute -bottom-1 -left-1 w-3 h-3 sm:w-4 sm:h-4 bg-yellow-300 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
                </motion.div>
                
                <div className="text-white">
                  <div className="inline-block bg-yellow-400 text-black font-black text-[10px] sm:text-sm px-2 sm:px-3 py-1 rounded-full mb-2 animate-pulse">
                    AVANCE IMMÉDIATE
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-4xl font-black mb-2 leading-tight">
                    Crédit d'impôt instantané !
                  </h3>
                  <p className="text-white/90 text-sm sm:text-base md:text-lg max-w-md">
                    Ne payez que <span className="font-bold text-yellow-300">la moitié du prix</span> de vos cours à domicile. 
                    Pas d'avance, pas d'attente !
                  </p>
                </div>
              </div>
              
              {/* Right side - CTA */}
              <div className="flex flex-col items-center gap-3 w-full lg:w-auto">
                <a
                  href="#contact"
                  data-testid="services-credit-impot-cta"
                  className="group w-full lg:w-auto justify-center bg-white hover:bg-yellow-300 text-gray-900 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all hover:scale-110 shadow-xl flex items-center gap-2"
                >
                  <span>J'en profite maintenant</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block text-blue-600 font-semibold mb-4 tracking-wider uppercase text-sm bg-blue-50 px-4 py-2 rounded-full">
            Nos Services
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-blue-900 mb-4 md:mb-6">
            Un accompagnement complet
          </h2>
          <p className="text-base md:text-lg text-blue-800/70 max-w-2xl mx-auto mb-6">
            Des solutions adaptées à chaque besoin pour accompagner vos enfants 
            vers la réussite scolaire et l'épanouissement personnel.
          </p>
          {/* Pricing Badge */}
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-6 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 text-white rounded-3xl sm:rounded-full px-6 sm:px-10 py-4 shadow-lg">
            <div className="text-xs sm:text-sm font-semibold uppercase tracking-wider opacity-90">À partir de</div>
            <div className="text-3xl font-black">20€<span className="text-xl font-bold">/h</span></div>
            <div className="text-xs sm:text-sm opacity-90">après réduction d'impôts*</div>
          </div>
        </motion.div>

        {/* Services Grid - Row 1: 4 services */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6"
        >
          {row1Services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={item}
              data-testid={`service-card-${index}`}
              className={`service-card group relative p-6 rounded-2xl border-2 border-gray-100 ${service.borderColor} ${!service.tallyFormId ? 'cursor-pointer' : ''}`}
              onClick={() => {
                if (!service.tallyFormId) {
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              {/* Icon */}
              <div className={`service-icon w-12 h-12 ${service.color} rounded-xl flex items-center justify-center mb-4 transition-all group-hover:scale-110`}>
                <service.icon className={`w-6 h-6 ${service.iconColor}`} />
              </div>

              {/* Content */}
              <h3 className="text-lg font-bold text-blue-900 mb-2 group-hover:text-blue-600 transition-colors">
                {service.title}
              </h3>
              <p className="text-sm text-blue-800/60 leading-relaxed mb-3">
                {service.description}
              </p>

              {/* Button - Tally for Stages, Contact for others */}
              {service.tallyFormId ? (
                <button
                  data-tally-open={service.tallyFormId}
                  data-tally-layout="modal"
                  data-tally-width="500"
                  data-tally-auto-close="3000"
                  className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all hover:scale-105 shadow-md"
                >
                  <span>Réserver en ligne</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex items-center text-orange-500 font-medium opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                  <span className="text-xs">Demander un devis</span>
                  <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Services Grid - Row 2: 3 services centered */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 max-w-5xl mx-auto"
        >
          {row2Services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={item}
              data-testid={`service-card-row2-${index}`}
              className={`service-card group relative p-6 rounded-2xl border-2 border-gray-100 ${service.borderColor}`}
            >
              {/* Icon */}
              <div className={`service-icon w-12 h-12 ${service.color} rounded-xl flex items-center justify-center mb-4 transition-all group-hover:scale-110`}>
                <service.icon className={`w-6 h-6 ${service.iconColor}`} />
              </div>

              {/* Content */}
              <h3 className="text-lg font-bold text-blue-900 mb-2 group-hover:text-blue-600 transition-colors">
                {service.title}
                {service.badge && (
                  <span className="ml-2 inline-block text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                    {service.badge}
                  </span>
                )}
              </h3>
              <p className="text-sm text-blue-800/60 leading-relaxed mb-3">
                {service.description}
              </p>

              {/* Button - Link for English Class, Tally for stages, Contact for others */}
              {service.link ? (
                <a
                  href={service.link}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all hover:scale-105 shadow-md"
                >
                  <span>En savoir plus</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : service.tallyFormId ? (
                <button
                  data-tally-open={service.tallyFormId}
                  data-tally-layout="modal"
                  data-tally-width="500"
                  data-tally-auto-close="3000"
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all hover:scale-105 shadow-md"
                >
                  <span>Réserver en ligne</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <div 
                  className="flex items-center text-orange-500 font-medium cursor-pointer"
                  onClick={() => {
                    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span className="text-xs">Demander un devis</span>
                  <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg"
          >
            <span>Demander un devis gratuit</span>
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
